
import {
  Vue, Component, Prop, Watch, Mixins,
} from 'vue-property-decorator';
import moment from 'moment';
import UtilsMixin from '@/mixins/UtilsMixin.vue';
import AttachmentsList from '@/components/AttachmentsList.vue';
import {
  Task, Lead, LeadStatus, PermissionCode, LeadType, WarehouseType, TaskStatus,
  AttachmentType, Attachment, ProjectsPool, ConfirmDialogOptions,
} from '@/types';
import TaskStatusAlert from '@/components/TaskStatusAlert.vue';
import TaskActions from '@/components/TaskActions.vue';
import SwitchWithConfirm from '@/components/SwitchWithConfirm.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import WeekPickerDialog from '@/components/WeekPickerDialog.vue';
import {
  currentUserHasPermission,
  getLeadCustomValueBoolean, getLeadCustomValueText, getLeadCustomValueDate,
} from '@/helpers';
import UTILS from '@/lib/UTILS';
import API from '@/lib/API';

moment.locale('it');

@Component({
  // mixins: [UtilsMixin],
  components: {
    TaskStatusAlert,
    TaskActions,
    SwitchWithConfirm,
    AttachmentsList,
    ConfirmDialog,
    DatePickerDialog,
    WeekPickerDialog,
  },
})
export default class TaskM extends Mixins(UtilsMixin) {
  @Prop(Object) readonly task: Task | undefined;
  @Prop({ type: Boolean, default: false }) readonly embedded!: boolean;
  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean;

  // Il form è valido?
  isFormValid = false

  selectedStatus: 'completed' | 'error' = 'completed'

  partialShipment = false
  partialShipmentDate: Date | null = null
  partialShipmentCompleted = false
  partialShipmentCompletedDate: Date | null = null

  shipmentPicked = false
  shipmentPickedDate: Date | null = null
  shipmentReady = false
  destination: string | null | undefined = null
  notes: string | null | undefined = null
  deliveryDate: Date | null = null

  shipmentCompleted = false
  shipmentCompletedDate: Date | null = null

  eWarehouse: WarehouseType | null = null;
  eWarehouseDate: Date | null = null
  eWarehouseUpdatedBy: string | null = null

  tWarehouse: WarehouseType | null = null;
  tWarehouseDate: Date | null = null
  tWarehouseUpdatedBy: string | null = null

  eMaterialsTargetDate: Date | null = null
  tMaterialsTargetDate: Date | null = null
  eMaterialsTargetDateViewMode: string | null | undefined = null
  tMaterialsTargetDateViewMode: string | null | undefined = null

  datePickerDialog = false
  weekPickerDialog = false
  datePickerDate: string | null = null
  datePickerName: string | null = null

  confirmDialog = false
  confirmOptions: ConfirmDialogOptions = {
    title: 'Attenzione',
    text: 'Stai pianificando una spedizione finale, ma il materiale non è completamente in magazzino.',
    question: 'Vuoi modificare la disponibilità materiale impostandola su "In magazzino"?',
    yesText: 'Sì, modificala',
    noText: 'No, lasciala com\'è',
  }

  created() {
    console.log(`${this.constructor.name}: created()`); // eslint-disable-line no-console

    // Inizializza tutti i campi del modulo
    this.refreshTask();
  }

  // Aggiorna i campi con gli eventuali nuovi valori della Lead selezionata
  async refreshTask() {
    console.log(`${this.constructor.name}: refreshTask()`); // eslint-disable-line no-console

    this.partialShipment = this.selectedLead?.partialShipment ?? false;
    this.partialShipmentDate = getLeadCustomValueDate(this.selectedLead, 'partialShipmentDate');
    this.partialShipmentCompleted = getLeadCustomValueBoolean(this.selectedLead, 'partialShipmentCompleted') ?? false;
    this.partialShipmentCompletedDate = getLeadCustomValueDate(this.selectedLead, 'partialShipmentCompletedDate');

    this.shipmentPicked = this.selectedLead?.shipmentPicked ?? false;
    this.shipmentPickedDate = getLeadCustomValueDate(this.selectedLead, 'shipmentPickedDate');
    this.shipmentReady = this.selectedLead?.shipmentReady ?? false;
    this.destination = getLeadCustomValueText(this.selectedLead, 'shipmentDestination');
    this.notes = getLeadCustomValueText(this.selectedLead, 'shipmentNotes');
    this.deliveryDate = getLeadCustomValueDate(this.selectedLead, 'deliveryDate');

    this.shipmentCompleted = getLeadCustomValueBoolean(this.selectedLead, 'shipmentCompleted') ?? false;
    this.shipmentCompletedDate = getLeadCustomValueDate(this.selectedLead, 'shipmentCompletedDate');

    this.eWarehouse = getLeadCustomValueText(this.selectedLead, 'eWarehouse') as WarehouseType | null;
    this.eWarehouseDate = getLeadCustomValueDate(this.selectedLead, 'eWarehouseDate');
    this.eWarehouseUpdatedBy = await this.getUserNameFromCustomValue('eWarehouseUpdatedBy');

    this.tWarehouse = getLeadCustomValueText(this.selectedLead, 'tWarehouse') as WarehouseType | null;
    this.tWarehouseDate = getLeadCustomValueDate(this.selectedLead, 'tWarehouseDate');
    this.tWarehouseUpdatedBy = await this.getUserNameFromCustomValue('tWarehouseUpdatedBy');

    this.eMaterialsTargetDate = getLeadCustomValueDate(this.selectedLead, 'eMaterialsTargetDate');
    this.tMaterialsTargetDate = getLeadCustomValueDate(this.selectedLead, 'tMaterialsTargetDate');
    this.eMaterialsTargetDateViewMode = getLeadCustomValueText(this.selectedLead, 'eMaterialsTargetDateViewMode') ?? 'week';
    this.tMaterialsTargetDateViewMode = getLeadCustomValueText(this.selectedLead, 'tMaterialsTargetDateViewMode') ?? 'week';
  }

  // Il Task è valido quando sia il form che i dati extra lo sono
  get valid(): boolean {
    return this.isFormValid && this.isExtraDataValid;
  }

  // I dati extra sono validi?
  get isExtraDataValid(): boolean {
    // Se è impossibile completare il task, i dati extra sono ignorati
    // FIXME: Rimuovere quando questo Task userà il mixin TaskMixin
    if (this.selectedStatus === 'error') return true;

    if (!this.hasAttachments('ddt')) return false;

    // In tutti gli altri casi...
    return true;
  }

  // Se la validità del form cambia, la memorizza nello stato di Vuex
  @Watch('valid')
  onValidChange(val: boolean) {
    console.log(`${this.constructor.name}: onValidChange() val =`, val); // eslint-disable-line no-console

    this.$store.dispatch('updateTaskFieldsValidity', { value: val });
  }

  get selectedLead(): Lead | null {
    const childLeads: ProjectsPool = this.$store.getters.currentChildren;
    const childLeadsInfo: ProjectsPool = this.$store.getters.currentChildrenInfo;

    // Se è stato specificato un Task e questo è associato ad una Lead figlia...
    if (this.task && this.task.relationship === 'father') {
      // ... se la lead figlia non è stata ancora assegnata,
      // non ritorna nulla.
      if ('childLeadId' in this.task === false || !this.task.childLeadId) return null;

      // ... se la lead figlia è stata assegnata,
      // considera i suoi dati (se disponibili,
      // altrimenti utilizza i dati limitati di childLeadsInfo)
      return childLeads[this.task.childLeadId] ?? childLeadsInfo[this.task.childLeadId] ?? null;
    }

    // Negli altri casi ritorna la Lead selezionata memorizzata tramite Vuex
    return this.$store.getters.currentLead;
  }

  get selectedTask(): Task | null {
    // Se non è stato specificato un Task nelle Prop,
    // utilizza il Task selezionato memorizzato tramite Vuex.
    return this.task ?? this.$store.getters.selectedTask;
  }

  get loading(): boolean {
    // Se è stato specificato un Task nelle Prop,
    // non utilizza questo boolean (sempre false).
    return this.task
      ? false
      : this.$store.getters.isTaskLoading;
  }

  get updating(): boolean {
    // Se è stato specificato un Task nelle Prop,
    // non utilizza questo boolean (sempre false).
    return this.task
      ? false
      : this.$store.getters.isTaskUpdating;
  }

  // Ritorma true se è in corso l'aggiornamento della Lead mostrata
  get leadUpdating(): boolean { return this.$store.getters.isProjectUpdating; }

  // Tiene sotto controllo leadUpdateing per capire quando eventualmente
  // vanno aggiornati i dati visualizzati.
  @Watch('leadUpdating')
  onLeadUpdatingChange(val: boolean, oldVal: boolean) {
    console.log(`${this.constructor.name}: onLeadUpdatingChange() val = ${val} oldVal = ${oldVal}`); // eslint-disable-line no-console

    // Se leadUpdating passa da true a folse,
    // la Lead ha completato l'aggiornamento e vanno aggiornati i dati visualizzati.
    if (val === false && oldVal === true) {
      // Aggiorna la Lead con i nuovi valori soltanto se è in visualizzazione readonly
      if (this.readonly) this.refreshTask();
    }
  }

  get leadType(): LeadType | null { return this.selectedLead?.type ?? null; }

  get leadStatus(): LeadStatus | null { return this.selectedLead?.status ?? null; }

  get taskStatus(): TaskStatus { return this.selectedTask?.status ?? null; }

  get form(): Vue & { validate: () => boolean; reset: () => boolean } {
    return this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };
  }

  get appendIcon(): string | null { return this.fieldsDisabled ? 'mdi-lock' : null; }

  get fieldsDisabled(): boolean {
    return (this.task !== null && this.task !== undefined)
      || this.selectedTask?.status !== null
      || this.readonly
      || this.loading
      || this.updating;
  }

  get attachments(): Attachment[] { return this.selectedLead?.Attachments ?? []; }

  // Mostra le azioni nell'Alert soltanto sulle lead figlie e sui Task senza figlie
  get showAlertActions(): boolean { return this.task?.relationship !== 'father'; }

  // Mostra le azioni
  get showActions(): boolean { return !this.task && !this.readonly && !this.embedded; }

  // FIXME: Implementare tramite Vuex
  async getUserNameFromCustomValue(name: string): Promise<string | null> {
    if (!this.selectedLead) return null;

    const userId = UTILS.getCustomValueUserIdByName(this.selectedLead, name) ?? null;
    const userName = userId ? await API.getUserName(userId) : null;
    return userName;
  }

  warehouseText(value: WarehouseType | null): string {
    switch (value) {
      case 'yes': return 'in magazzino';
      case 'partial': return 'parziale in magazzino';
      case 'no': return 'non in magazzino';
      default: return 'non definita';
    }
  }

  get eWarehouseText(): string { return this.warehouseText(this.eWarehouse); }

  get tWarehouseText(): string { return this.warehouseText(this.tWarehouse); }

  planDateString(date: Date | null | undefined, viewMode: string): string | null {
    switch (viewMode) {
      case 'eMaterialsTargetDateViewMode':
        return this.eMaterialsTargetDateViewMode === 'week' ? this.mWeek(date) : this.mDate(date);

      case 'tMaterialsTargetDateViewMode':
        return this.tMaterialsTargetDateViewMode === 'week' ? this.mWeek(date) : this.mDate(date);

      default:
        return null;
    }
  }

  initialPickerMode(): string | null | undefined {
    switch (this.datePickerName) {
      case 'eMaterialsTargetDate':
        return this.eMaterialsTargetDateViewMode;

      case 'tMaterialsTargetDate':
        return this.tMaterialsTargetDateViewMode;

      default:
        return 'week';
    }
  }

  hasPermission(codename: PermissionCode) { return currentUserHasPermission(codename); }

  hasAttachments(fieldname: AttachmentType): boolean {
    return this.attachments.filter((a) => a.fieldname === fieldname).length > 0;
  }

  onSwitchChange(name: string) {
    console.log(`${this.constructor.name}: onSwitchChange(${name})`); // eslint-disable-line no-console

    // Se lo switch non esiste si ferma subito
    if (name in this === false) { return; }

    // Il valore del modello dello switch è già stato aggiornato.
    // Proseguo aggiornando eventuali valori collegati e preparo l'aggiornamento tramite API.

    const update = {} as { [key: string]: unknown };

    update[name] = this[name];

    // Modifico eventuali campi collegati
    switch (name) {
      case 'partialShipment':
        if (this.partialShipment) {
          this.partialShipmentDate = moment(moment(), 'YYYY-MM-DD').toDate();
          update.partialShipmentDate = this.partialShipmentDate;
        } else {
          this.partialShipmentDate = null;
          update.partialShipmentDate = null;
        }
        break;

      case 'shipmentPicked':
        if (this.shipmentPicked) {
          this.shipmentPickedDate = moment(moment(), 'YYYY-MM-DD').toDate();
          update.shipmentPickedDate = this.shipmentPickedDate;
        } else {
          this.shipmentPickedDate = null;
          update.shipmentPickedDate = null;
        }
        break;

      case 'partialShipmentCompleted':
        if (this.partialShipmentCompleted) {
          this.partialShipmentCompletedDate = moment(moment(), 'YYYY-MM-DD').toDate();
          update.partialShipmentCompletedDate = this.partialShipmentCompletedDate;
        } else {
          this.partialShipmentCompletedDate = null;
          update.partialShipmentCompletedDate = null;
        }
        break;

      case 'shipmentReady':
        // Se la spedizione finale è stata impostata su true...
        if (this.shipmentReady === true) {
          // ... Se la disponibilità del materiale non è 'in magazzino'...
          if (this.leadType === 'elettrica-figlia' && this.eWarehouse !== 'yes') {
            // ... Mostra il dialog che chiede se vuoi anche impostarla
            this.confirmDialog = true;
          } else
          if (this.leadType === 'termica-figlia' && this.tWarehouse !== 'yes') {
            // ... Mostra il dialog che chiede se vuoi anche impostarla
            this.confirmDialog = true;
          }
        }
        break;

      case 'shipmentCompleted':
        if (this.shipmentCompleted) {
          this.shipmentCompletedDate = moment(moment(), 'YYYY-MM-DD').toDate();
          update.shipmentCompletedDate = this.shipmentCompletedDate;
        } else {
          this.shipmentCompletedDate = null;
          update.shipmentCompletedDate = null;
        }
        break;

      default:
        break;
    }

    this.$store.dispatch('updateTaskFields', update);
  }

  onClickDate(name: string) {
    console.log(`${this.constructor.name}: onClickDate(${name})`); // eslint-disable-line no-console

    if (this.leadStatus !== 'open'
      || this.taskStatus !== null
      || this.readonly) return;

    // Imposta per quale campo verrà utilizzato il DatePicker
    this.datePickerName = name;

    // Imposta l'eventuale data precedentemente selezionata
    this.datePickerDate = this[name]
      ? moment(this[name]).toISOString(true).substring(0, 10)
      : null;

    // Apre il dialog per la selezione della data o della settimana
    switch (name) {
      case 'eMaterialsTargetDate':
      case 'tMaterialsTargetDate':
        this.weekPickerDialog = true;
        break;

      default:
        this.datePickerDialog = true;
        break;
    }
  }

  onClearDate(name: string) {
    console.log(`${this.constructor.name}: onClearDate(${name})`); // eslint-disable-line no-console

    if (this.leadStatus !== 'open') return;

    const update = {} as { [key: string]: unknown };

    // Cancello la data nel modulo e nel prossimo aggiornamento
    if (name in this) {
      this[name] = null;
      update[name] = null;
    }

    // Modifico eventuali campi collegati
    switch (name) {
      default:
        break;
    }

    // Valido il modulo con le nuove informazioni inserite
    this.onValidate('completed');

    this.$store.dispatch('updateTaskFields', update);
  }

  onSelectDate(date: string | null, mode: string | null) {
    console.log(`${this.constructor.name}: onSelectDate() date=${date}, mode=${mode}`); // eslint-disable-line no-console

    if (!this.datePickerName) return;

    // Chiudo il dialog per la selezione della data o della settimana
    this.datePickerDialog = false;
    this.weekPickerDialog = false;

    const update = {} as { [key: string]: unknown };

    // Trasforma in un valore di tipo Date prima di restituirlo
    const selectedDate = date
      ? moment(date, 'YYYY-MM-DD').toDate()
      : null;

    // Inserisco la data nel modulo e nell'aggiornamento
    if (this.datePickerName in this) {
      this[this.datePickerName] = selectedDate;
      update[this.datePickerName] = this[this.datePickerName];
    }

    // Modifico eventuali campi collegati
    switch (this.datePickerName) {
      case 'eMaterialsTargetDate':
        this.eMaterialsTargetDateViewMode = mode;
        update.eMaterialsTargetDateViewMode = this.eMaterialsTargetDateViewMode;
        break;

      case 'tMaterialsTargetDate':
        this.tMaterialsTargetDateViewMode = mode;
        update.tMaterialsTargetDateViewMode = this.tMaterialsTargetDateViewMode;
        break;

      default:
        break;
    }

    // Valido il modulo con le nuove informazioni inserite
    this.onValidate('completed');

    this.$store.dispatch('updateTaskFields', update);
  }

  onFieldUpdated(field: string) {
    console.log(`${this.constructor.name}: onFieldUpdated(${field})`); // eslint-disable-line no-console

    const update = {} as { [key: string]: unknown };

    // Aggiorna sempre il campo specificato
    update[field] = this[field] ?? '';

    // Aggiorna eventuali campi collegati, eccezioni, ecc.
    switch (field) {
      case 'eWarehouse':
        // Memorizzo la data attuale e l'utente che ha fatto la modifica.
        // Questa memorizzazione è solo lato client e non viene inviata al server
        // perché la data e l'utente vengono memorizzati dal server.
        this.eWarehouseDate = new Date();
        this.eWarehouseUpdatedBy = this.currentUser.name;
        break;

      case 'tWarehouse':
        // Memorizzo la data attuale e l'utente che ha fatto la modifica.
        // Questa memorizzazione è solo lato client e non viene inviata al server
        // perché la data e l'utente vengono memorizzati dal server.
        this.tWarehouseDate = new Date();
        this.tWarehouseUpdatedBy = this.currentUser.name;
        break;

      default:
        break;
    }

    this.$store.dispatch('updateTaskFields', update);
  }

  // Effetta la validazione del modulo
  async onValidate(status: 'completed' | 'error') {
    console.log(`${this.constructor.name}: onValidate(${status})`); // eslint-disable-line no-console

    // Sceglie se validare il modulo per un completamento o per un fallimento
    this.selectedStatus = status;

    // Avvia l'effettiva validazione
    const form = this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };

    if (form) {
      const result = form.validate();
      console.log(`${this.constructor.name}: onValidate() form.validate() =`, result); // eslint-disable-line no-console
    } else {
      console.error(`${this.constructor.name}: onValidate() form =`, form); // eslint-disable-line no-console
      console.error(`${this.constructor.name}: onValidate() this.$refs =`, this.$refs); // eslint-disable-line no-console
    }

    return Promise.resolve();
  }

  onEdit() {
    console.log(`${this.constructor.name}: onEdit()`); // eslint-disable-line no-console

    this.$emit('edit');
  }

  onReset() {
    console.log(`${this.constructor.name}: onReset()`); // eslint-disable-line no-console

    this.$emit('reset');
  }

  async onConfirm() {
    console.log(`${this.constructor.name}: onConfirm()`); // eslint-disable-line no-console

    // Imposta la disponibilità materiale su "In magazzino"
    if (this.leadType === 'elettrica-figlia') {
      this.eWarehouse = 'yes';
      this.onFieldUpdated('eWarehouse');
    } else
    if (this.leadType === 'termica-figlia') {
      this.tWarehouse = 'yes';
      this.onFieldUpdated('tWarehouse');
    }
  }
}
